import { useRef, useState, useCallback } from 'react';
import useGmailStore from '../../store/useGmailStore';
import useClickOutside from '../../hooks/useClickOutside';
import Spinner from '../../components/common/Spinner';
import { IconChevron } from '../../components/icons';

const GoogleG = () => (
  <svg width="14" height="14" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

/* "Connect Gmail" → Google OAuth (client-side only). Connected state shows the
   account's email with a menu to disconnect. Lives in the Topbar, so every page
   that sends from Gmail shares one connect/switch control; phones keep just the
   mark (.gmail-lbl hides). */
export default function ConnectGmail() {
  const { account, connecting, connect, disconnect } = useGmailStore();
  const [open, setOpen] = useState(false);
  /* Google's avatar URLs 403 for some accounts/referrers — fall back to the
     Google mark rather than leaving a broken-image icon in the pill */
  const [avatarFailed, setAvatarFailed] = useState(false);
  const ref = useRef(null);

  useClickOutside(ref, useCallback(() => setOpen(false), []));

  if (!account) {
    return (
      <button
        className="btn btn-ghost btn-sm gmail-connect"
        disabled={connecting}
        title="Connect Gmail — emails you send from here go out from this account"
        onClick={connect}
      >
        {connecting ? (
          <><Spinner variant="green" /> <span className="gmail-lbl">Connecting…</span></>
        ) : (
          <><GoogleG /> <span className="gmail-lbl">Connect Gmail</span></>
        )}
      </button>
    );
  }

  return (
    <div className="rich-select gmail-connect" style={{ width: 'auto' }} ref={ref}>
      <button className="btn btn-ghost btn-sm" title={`Sending as ${account.email}`} onClick={() => setOpen((o) => !o)}>
        {account.picture && !avatarFailed ? (
          <img
            src={account.picture}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setAvatarFailed(true)}
            style={{ width: 16, height: 16, borderRadius: '50%' }}
          />
        ) : (
          <GoogleG />
        )}
        <span className="gmail-lbl">{account.email}</span>
        <IconChevron />
      </button>
      {open && (
        <div className="rich-select-menu open" style={{ left: 'auto', right: 0, width: 260 }}>
          <div style={{ padding: '8px 9px', fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
            Connected as <b style={{ color: 'var(--ink)' }}>{account.email}</b>
          </div>
          <div
            className="rich-select-item"
            style={{ color: 'var(--red)', fontWeight: 600 }}
            onClick={() => {
              disconnect();
              setOpen(false);
            }}
          >
            Disconnect Gmail
          </div>
        </div>
      )}
    </div>
  );
}
